import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export type ActivityCategory = 'attendance' | 'document' | 'notification' | 'schedule'

export type ActivityAction =
  | 'check_in'
  | 'check_out'
  | 'break_start'
  | 'break_end'
  | 'auto_close'
  | 'document_view'
  | 'document_created'
  | 'notification_read'
  | 'notification_sent'
  | 'exception_created'
  | 'shift_limit_blocked'
  | 'shift_limit_override'

export interface ActivityLogEmployee {
  id: string
  first_name: string
  last_name: string
  middle_name?: string
  employee_number: string
}

export interface ActivityLogDetails {
  location_id?: string
  location_name?: string
  document_id?: string
  document_title?: string
  document_type?: string
  notification_id?: string
  notification_title?: string
  notification_type?: string
  lat?: number
  lng?: number
  source_type?: string
  performed_by?: string
  exception_date?: string
  limit_details?: string
}

export interface ActivityLog {
  id: string
  timestamp: string
  category: ActivityCategory
  action: ActivityAction
  employee_id: string
  employee: ActivityLogEmployee
  details: ActivityLogDetails
  source_table: string
  source_id: string
}

export interface ActivityLogFilters {
  employeeId?: string
  dateFrom?: string
  dateTo?: string
  category?: ActivityCategory
  action?: ActivityAction
  page?: number
  limit?: number
  sort?: 'ASC' | 'DESC'
}

export interface PaginatedActivityLogs {
  data: ActivityLog[]
  total: number
  page: number
  limit: number
  totalPages: number
}

const ACTIVITY_LOGS_QUERY_KEY = 'activity-logs'

export const useActivityLogs = (filters: ActivityLogFilters = {}) => {
  return useQuery({
    queryKey: [ACTIVITY_LOGS_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedActivityLogs> => {
      const response = (await apiClient.get('/api/v1/activity-logs', {
        params: filters,
      })) as {
        data: ActivityLog[]
        meta?: { total: number; page: number; limit: number; totalPages: number }
      }
      const meta = response.meta || { total: 0, page: 1, limit: 20, totalPages: 1 }
      return {
        data: response.data || [],
        total: meta.total,
        page: meta.page,
        limit: meta.limit,
        totalPages: meta.totalPages,
      }
    },
    staleTime: 30 * 1000,
    retry: 3,
  })
}

// Helper labels for UI
export const ACTIVITY_CATEGORY_LABELS: Record<ActivityCategory, string> = {
  attendance: 'Посещаемость',
  document: 'Документы',
  notification: 'Уведомления',
  schedule: 'График',
}

export const ACTIVITY_ACTION_LABELS: Record<ActivityAction, string> = {
  check_in: 'Приход',
  check_out: 'Уход',
  break_start: 'Начало перерыва',
  break_end: 'Конец перерыва',
  auto_close: 'Авто-закрытие',
  document_view: 'Просмотр документа',
  document_created: 'Документ создан',
  notification_read: 'Прочтение уведомления',
  notification_sent: 'Отправка уведомления',
  exception_created: 'Исключение создано',
  shift_limit_blocked: 'Блокировка: лимит смен',
  shift_limit_override: 'Обход лимита админом',
}
